<template>
  <div class="explore-memberships text-neutral-800 pb-20">
    <NavbarWithBackButton @click="onNavbarWithBackButtonClick" />
    <div class="grid-container md:px-5">
      <div v-if="showDeals" class="p-4 pb-0 md:pl-0 md:pt-0">
        <h1 class="o-heading-2 md:mb-9 md:mt-12" v-text="getTranslations('Title')" />
      </div>
      <div
        ref="pickMembershipSlider"
        class="pick-membership-slider pb-4 pt-4 flex bg-white overflow-x-scroll pr-4 disable-scrollbars horizontal-scroll md:hidden"
      >
        <div v-for="button in navigationButtons" :key="button.value" class="scroll-item-start flex-shrink-0 pl-4">
          <NewButton v-bind="getButtonProps(button)" @click="onMembershipClick(button)">
            {{ button.label }}
          </NewButton>
        </div>
      </div>
      <div v-loading="loading">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="deal in deals"
            :key="`${deal.id}-deal`"
            :ref="`deal-${deal.reference}`"
          >
            <DealShowcase
              :id="deal.reference"
              :deal="deal"
              :latest-designs="latestDesigns"
              :country="country"
              :locale="locale"
              has-discount
              @select="onDealSelect"
            />
          </div>
        </div>
      </div>
      <div v-if="showSubscriptions" class="p-4 pb-3 md:pl-0">
        <h2
          class="o-heading-2 md:mb-9"
          :class="{
            'md:mt-20': showDeals,
            'md:mt-4': !showDeals
          }"
          v-text="getTranslations('AlsoTry')"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="subscription in subscriptions"
          :key="`${subscription.id}-subscription`"
          :ref="`deal-${subscription.reference}`"
        >
          <DealShowcase
            :id="subscription.reference"
            :deal="subscription"
            :latest-designs="latestDesigns"
            :country="country"
            :locale="locale"
            @select="onDealSelect"
          />
        </div>
      </div>
      <div class="p-4 mt-7 max-120">
        <p class="o-heading-2 mb-4 font-24" v-text="$t('Account.Home.NewFaq.Title')" />
        <Faq
          data-qa="add-membership-faq"
          :items="faqItems"
        />
      </div>
      <OFooter
        :locale="locale"
        :year="$moment().format('YYYY')"
        :faq-column="footer.faqColumn"
        :how-does-it-work-column="footer.howDoesItWorkColumn"
        :legal-column="footer.legalColumn"
      />
    </div>
    <HasAddedNewMembershipModal
      v-if="renderHasAddedNewMembershipModal"
      ref="hasAddedNewMembershipModal"
      v-bind="hasAddedNewMembershipModalProps"
    />
    <LoginModal
      ref="loginModal"
      :country="country"
      @success="onSuccessLogin"
    />
    <SizeChartModal
      ref="SizeChartModal"
      :country="country"
    />
  </div>
</template>

<script>
import Faq from '@/components/Faq'
import NavbarWithBackButton from '@/components/NavbarWithBackButton'
import NewButton from '@/components/NewButton'
import DealShowcase from '@/components/DealShowcase'
import HasAddedNewMembershipModal from '@/components/Modals/HasAddedNewMembershipModal'
import LoginModal from '@/components/Modals/LoginModal'
import { SIGNUP_GET_LATEST_DESIGN, GET_UPSELL_PRODUCTS } from '@/utils/store/actionTypes'
import { mapActions, mapState, mapGetters } from 'vuex'
import { Orientations } from '@/enums/products'
import { formatGetLatestDesignResponseWithAllData } from '@/utils/designs'
import { addLocalCurrency, serializeParameters } from '@/utils/general'
import OFooter from '@/components/OFooter'
import footerMixin from '@/mixins/footer'
import { AUTH_ID } from '@/utils/store/getterTypes'
import SizeChartModal from '@/components/Women/SizeChartModal'
import { Currencies } from '@/enums/general'

export default {
  name: 'ExploreMemberships',
  middleware: 'homepage',
  layout: 'temp',
  components: {
    NavbarWithBackButton,
    NewButton,
    DealShowcase,
    OFooter,
    Faq,
    HasAddedNewMembershipModal,
    LoginModal,
    SizeChartModal
  },
  mixins: [footerMixin],
  async fetch () {
    this.loading = true
    await Promise.all([
      this.getAndSetLatestDesigns(),
      this.getAndSetUpsellProducts()
    ])
    this.loading = false
  },
  data () {
    return {
      deals: [
        {
          id: 1,
          name: 'originals-socks-regular-membership',
          reference: 'originals-socks-regular-membership',
          campaignEligible: true,
          campaignStrategy: 'extra_order'
        },
        {
          id: 2,
          name: 'originals-socks-sneaker-membership',
          reference: 'originals-socks-sneaker-membership',
          campaignEligible: true,
          campaignStrategy: 'extra_order'
        },
        {
          id: 3,
          name: 'men-undershirts-membership',
          reference: 'men-undershirts-membership',
          campaignEligible: false,
          campaignStrategy: null
        }
      ],
      subscriptions: [
        {
          id: 4,
          name: 'men-originals-membership',
          reference: 'men-originals-membership',
          campaignEligible: false,
          campaignStrategy: null
        },
        {
          id: 5,
          name: 'men-one-membership',
          reference: 'men-one-membership',
          campaignEligible: false,
          campaignStrategy: null
        },
        {
          id: 6,
          name: 'boys-originals-membership',
          reference: 'boys-originals-membership',
          campaignEligible: false,
          campaignStrategy: null
        },
        {
          id: 7,
          name: 'women-membership',
          reference: 'women-membership',
          campaignEligible: false,
          campaignStrategy: null
        }
      ],
      activeMembership: {
        id: 1,
        name: 'originals-socks-regular-membership',
        reference: 'originals-socks-regular-membership',
        campaignEligible: true,
        campaignStrategy: 'extra_order'
      },
      latestDesignsResponse: [],
      observers: [],
      isProgrammaticScroll: false,
      loading: false
    }
  },
  computed: {
    showDeals () {
      return this.deals.length > 0
    },
    showSubscriptions () {
      return this.subscriptions.length > 0
    },
    renderHasAddedNewMembershipModal () {
      return (this.$route.query.action === 'addedNewMembership' || this.$route.query.action === 'hasReactivated') && this.$route.query.data
    },
    renderLoginModal () {
      return (!this.$route.query.userId || !this.$route.query.hash) && !this.userId
    },
    faqItems () {
      const path = 'ExploreMemberships.FAQ'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      const enrichedItems = items.map((_, i) => ({
        title: this.$t(`${path}[${i}].Title`, this.variables),
        description: this.$t(`${path}[${i}].Description`, this.variables)
      }))

      return enrichedItems
    },
    navigationButtons () {
      return [...this.deals, ...this.subscriptions].map(item => ({
        ...item,
        value: item.reference,
        label: this.$t(`MemberShipTypes.${item.reference}.Name`)
      }))
    },
    latestDesigns () {
      const latestDesigns = {
        [Orientations.womenhipster]: { slug: 'loua', primaryColor: '242,144,162' },
        [Orientations.womenbralette]: { slug: 'kate', primaryColor: '242,144,162' },
        [Orientations.boysoriginals]: { slug: 'pines', primaryColor: '57,74,59' },
        [Orientations.menone]: { slug: 'pines', primaryColor: '57,74,59' },
        [Orientations.menoriginals]: { slug: 'pines', primaryColor: '57,74,59' },
        [Orientations.socksoriginals]: { slug: 'pines', primaryColor: '57,74,59' },
        [Orientations.sneakersocksoriginals]: { slug: 'pines', primaryColor: '57,74,59' },
        [Orientations.undershirts]: { slug: 'salt', primaryColor: '226,166,21' },
        ...formatGetLatestDesignResponseWithAllData(this.latestDesignsResponse)
      }

      return latestDesigns
    },
    hasAddedNewMembershipModalProps () {
      try {
        const base64String = this.$route.query.data
        const hasReactivated = this.$route.query.action === 'hasReactivated'

        if (!base64String) {
          return {}
        }

        const jsonString = decodeURIComponent(atob(base64String))

        const data = JSON.parse(jsonString)
        const countryCode = `${this.locale}-${this.country}`

        return {
          name: data.memberName,
          membershipTypeReference: data.membershipType.reference,
          price: addLocalCurrency(data.membershipPrice.amount, countryCode, Currencies[this.country]),
          originalPrice: data.membershipPrice.originalAmount ? addLocalCurrency(data.membershipPrice.originalAmount, countryCode, Currencies[this.country]) : null,
          sizes: data.productGroupSizes.map(size => size.name),
          nextPaymentDate: data.nextOccurringDates.nextPaymentDate,
          nextDeliveryDate: data.nextOccurringDates.nextDeliveryDate,
          title: hasReactivated ? this.$t('MembershipStatusForm.ReactivatedPage.WelcomeBack') : this.$t('ExploreMemberships.Modals.HasAddedNewMembership.Title'),
          description: hasReactivated ? this.$t('Home.Reactivation.Success') : this.$t('ExploreMemberships.Modals.HasAddedNewMembership.Description')
        }
      } catch (error) {
        return {}
      }
    },
    variables () {
      const womenSize = `<a href="/women/bra-size">${this.$t('PrivacyStatement.General.Here')}</a>`
      const br = '<br />'
      const womenSizeChartButton = `<button id="women-size-chart-btn">${this.$t('PrivacyStatement.General.Here')}</button>`

      return {
        womenSize,
        br,
        womenSizeChartButton
      }
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    }),
    ...mapGetters('auth', {
      userId: AUTH_ID
    })
  },
  mounted () {
    setTimeout(() => {
      this.initIntersectionObservers()
    }, 2500)

    const isAddedNewMembershipAction = this.$route.query.action === 'addedNewMembership'
    const hasReactivated = this.$route.query.action === 'hasReactivated'
    const isDataPresent = this.$route.query.data

    if ((isAddedNewMembershipAction || hasReactivated) && isDataPresent) {
      setTimeout(() => {
        this.$refs.hasAddedNewMembershipModal.toggleActivation(true)
      }, 500)
    }

    if (this.renderLoginModal) {
      setTimeout(() => {
        this.$refs.loginModal.toggleActivation(true)
      }, 500)
    }

    setTimeout(() => {
      this.attachWomenSizeChartHandler()
    }, 1000)
  },
  beforeDestroy () {
    this.cleanupIntersectionObservers()
  },
  methods: {
    async getAndSetLatestDesigns () {
      try {
        const latestDesignsResponse = await this.getLatestDesign({
          women: true,
          men: true,
          addon: true,
          boys: true
        })

        this.latestDesignsResponse = latestDesignsResponse
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getAndSetUpsellProducts () {
      if (this.renderLoginModal) {
        return
      }

      try {
        const { deals, subscriptions } = await this.getUpsellProducts({ id: this.$route.query.userId || this.userId, hash: this.$route.query.hash })

        this.deals = deals
        this.subscriptions = subscriptions

        this.activeMembership = this.deals[0] || this.subscriptions[0] || this.activeMembership
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async onSuccessLogin () {
      try {
        this.loading = true

        await Promise.all([
          this.getAndSetLatestDesigns(),
          this.getAndSetUpsellProducts()
        ])

        this.$refs.loginModal.toggleActivation(false)

        this.loading = false
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    onDealSelect (deal) {
      if (this.renderLoginModal) {
        return
      }

      const params = serializeParameters({
        campaignStrategy: deal.campaignStrategy,
        combiDeal: deal.hasDiscount,
        design: deal.designSlug,
        userId: this.$route.query.userId,
        hash: this.$route.query.hash
      })

      this.$router.push(`/explore-memberships/${deal.reference}?${params}`)
    },
    onNavbarWithBackButtonClick () {
      this.$router.push('/')
    },
    getTranslations (path = '') {
      return this.$t(`ExploreMemberships.${path}`)
    },
    toggleWomenSizeChart () {
      this.$refs.SizeChartModal.toggleBralette()
    },
    getButtonProps (membership) {
      const isActive = this.activeMembership?.reference === membership.value

      return {
        color: isActive ? 'yellow' : 'white',
        variant: isActive ? 'solid' : 'outline'
      }
    },
    scrollToReference (reference) {
      const el = document.getElementById(reference)
      if (el) {
        const rect = el.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const targetY = rect.top + scrollTop - 129
        window.scrollTo({ top: targetY, behavior: 'smooth' })
      }
    },
    onMembershipClick (membership) {
      this.activeMembership = membership
      this.isProgrammaticScroll = true
      this.$nextTick(() => {
        this.scrollSliderToButton(membership.reference)
        this.scrollToReference(membership.reference)
        setTimeout(() => {
          this.isProgrammaticScroll = false
        }, 700)
      })
    },
    initIntersectionObservers () {
      this.observers = []

      // Observe deals
      this.deals.forEach((deal) => {
        const el = this.$refs[`deal-${deal.reference}`]?.[0]
        if (el) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (this.isProgrammaticScroll) { return }
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.activeMembership = deal
                  this.scrollSliderToButton(deal.reference)
                }
              })
            },
            { threshold: 0.9 } // Adjust as needed
          )

          observer.observe(el)
          this.observers.push(observer)
        }
      })

      // Observe subscriptions
      this.subscriptions.forEach((subscription) => {
        const el = this.$refs[`deal-${subscription.reference}`]?.[0]

        if (el) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (this.isProgrammaticScroll) { return }
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.activeMembership = subscription
                  this.scrollSliderToButton(subscription.reference)
                }
              })
            },
            { threshold: 0.9 }
          )

          observer.observe(el)
          this.observers.push(observer)
        }
      })
    },
    cleanupIntersectionObservers () {
      if (this.observers) {
        this.observers.forEach(observer => observer.disconnect())
        this.observers = []
      }
    },
    scrollSliderToButton (reference) {
      const slider = this.$refs.pickMembershipSlider
      if (!slider) { return }
      const buttonWrappers = slider.children
      const idx = this.navigationButtons.findIndex(btn => btn.value === reference)
      if (idx === -1) { return }
      const buttonEl = buttonWrappers[idx]
      if (!buttonEl) { return }
      slider.scrollTo({
        left: buttonEl.offsetLeft,
        behavior: 'smooth'
      })
    },
    attachWomenSizeChartHandler () {
      this.$nextTick(() => {
        const btn = document.getElementById('women-size-chart-btn')

        if (btn) {
          btn.addEventListener('click', () => {
            this.toggleWomenSizeChart()
          })
        }
      })
    },
    ...mapActions('signup', {
      getLatestDesign: SIGNUP_GET_LATEST_DESIGN
    }),
    ...mapActions('member', {
      getUpsellProducts: GET_UPSELL_PRODUCTS
    })
  }
}
</script>

<style scoped lang="postcss">
.explore-memberships {
  .font-14 {
    font-size: 1.4rem !important;
  }

  .font-24 {
    font-size: 2.4rem !important;
  }

  .pick-membership-slider {
    @apply sticky z-10;
    top: 5.4rem;

    .new-o-button >>> span {
      @apply font-semibold !important;
    }
  }

  >>> .footer-content-faq,
  >>> .footer-content-how-does-it-work,
  >>> .footer-content-legal {
    @apply hidden;
  }

  .grid-container {
    @apply mx-auto;
    max-width: 119.2rem;
  }
}

@screen lg {
  .explore-memberships {
    >>> .footer-content-faq,
    >>> .footer-content-how-does-it-work,
    >>> .footer-content-legal {
      @apply block;
    }

    .max-120 {
      @apply mx-auto;
      max-width: 70rem;
    }
  }
}
</style>
